/**
 * Shared with education `drug-table-ui.jsx` so the medication sidebar
 * highlights the same rows as the drug-class table (supports multiple selections).
 */

const NON_DRUG_TOKENS = new Set([
  "mg",
  "mcg",
  "g",
  "ml",
  "iu",
  "tab",
  "tabs",
  "tablet",
  "tablets",
  "cap",
  "caps",
  "capsule",
  "capsules",
  "po",
  "oral",
  "subcutaneous",
  "sublingual",
  "patch",
  "aerosol",
  "solution",
  "syrup",
  "injection",
  "long",
  "acting",
  "xl",
  "xr",
  "sr",
  "cd",
  "er",
  "dr",
  "cr",
  "hcl",
]);

export function normalizeDrugName(value = "") {
  const source = value.toString().toLowerCase();
  const noBrackets = source.replace(/\[[^\]]*]/g, " ").replace(/\([^)]*\)/g, " ");
  const clean = noBrackets.replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return "";

  const parts = clean.split(" ");
  const filtered = [];

  for (const part of parts) {
    if (!part) continue;
    if (/\d/.test(part)) break;
    if (NON_DRUG_TOKENS.has(part)) continue;
    filtered.push(part);
  }

  return (filtered.join(" ") || clean).trim();
}

function extractBracketAliases(value = "") {
  const source = value.toString();
  const matches = [
    ...source.matchAll(/\(([^)]*)\)/g),
    ...source.matchAll(/\[([^\]]*)\]/g),
  ];
  return matches
    .map((m) => normalizeDrugName(m?.[1] || ""))
    .filter(Boolean);
}

/** Combo-aware normalization for dropdown / option labels (single ingredient). */
export function getNormalizedLabelCandidates(value = "") {
  const base = normalizeDrugName(value);
  const aliases = extractBracketAliases(value);
  return Array.from(new Set([base, ...aliases].filter(Boolean)));
}

/** Combo med names: split on / | + so each ingredient normalizes for matching. */
export function getNormalizedMedicationCandidates(value = "") {
  const source = String(value || "").trim();
  if (!source) return [];

  const COMBO_SPLIT = /\s*(?:[/|]|\s\+\s)\s*/;
  const segments = source.split(COMBO_SPLIT).map((s) => s.trim()).filter(Boolean);

  const out = new Set();

  const addFromChunk = (chunk) => {
    const n = normalizeDrugName(chunk);
    if (n) out.add(n);
    extractBracketAliases(chunk).forEach((a) => {
      if (a) out.add(a);
    });
  };

  addFromChunk(source);
  if (segments.length > 1) {
    segments.forEach((seg) => addFromChunk(seg));
  }

  return Array.from(out).filter(Boolean);
}

function tokensMatchMedAndOption(medBase, optBase) {
  if (!medBase || !optBase) return false;
  if (medBase === optBase) return true;
  if (medBase.includes(optBase) || optBase.includes(medBase)) return true;

  const minLen = 4;
  const optTokens = optBase.split(/\s+/).filter((t) => t.length >= minLen);
  if (optTokens.some((t) => medBase.includes(t))) return true;

  const medTokens = medBase.split(/\s+/).filter((t) => t.length >= minLen);
  if (medTokens.some((t) => optBase.includes(t))) return true;

  return false;
}

/**
 * @param {Array<{ name?: string, value?: string }>} amrEducation - from drug table / Redux
 * @param {string|{ medicationName?: string, displayName?: string, name?: string }} medOrName
 */
export function medicationMatchesAmrEducation(amrEducation, medOrName) {
  if (!Array.isArray(amrEducation) || amrEducation.length === 0) return false;

  const raw =
    typeof medOrName === "string"
      ? medOrName
      : (medOrName?.medicationName ??
        medOrName?.displayName ??
        medOrName?.name ??
        "");

  const medCandidates = getNormalizedMedicationCandidates(raw);
  if (medCandidates.length === 0) return false;

  return amrEducation.some((opt) => {
    const optName = (opt?.name ?? opt?.value ?? "").toString();
    const optCandidates = getNormalizedLabelCandidates(optName);
    if (optCandidates.length === 0) return false;
    return medCandidates.some((medBase) =>
      optCandidates.some((optBase) => tokensMatchMedAndOption(medBase, optBase))
    );
  });
}
